<template>
  <div class="realtimeplot-container">
    <!-- 连接状态提示区域 -->
    <div v-if="isReconnecting" class="connection-status">
      <el-alert
        :title="reconnectMessage"
        type="warning"
        :closable="false"
        show-icon
      >
      </el-alert>
    </div>

    <!-- 图表区域 -->
    <div class="chart-container">
      <div ref="chartRef" style="width: 100%; height: 400px"></div>
    </div>

    <!-- 筛选条件和数据表格区域 -->
    <div class="control-panel">
      <!-- 筛选条件区域 -->

      <el-form :inline="true" class="filter-row">
        <el-form-item :label="$t('message.reaTimePlot.companyDeviceTree')">
          <!-- <el-cascader
            v-model="currentDeviceId"
            :data="deviceList"
            :options="deviceTreeOptions"
            :props="deviceTreeProps"
            clearable
            :placeholder="$t('message.reaTimePlot.selectDevice')"
            @change="handleDeviceChange"
            class="filter-control"
            style="width: 300px"
          ></el-cascader> -->
          <el-tree-select
                  v-model="currentDeviceId"
                  :data="deviceTreeOptions"
                  :props="treeSelectProps"
                  clearable
                  :placeholder="$t('message.realTimeData.selectDevice')"                  
                  @change="handleDeviceIdChange" 
                  @node-click="handleNodeClick"  
                  style="width: 200px"
                  check-strictly
                  :render-after-expand="false"
                   :expand-on-click-node="true" 
                   ref="treeSelectRef"
                >  
                <template #default="{ data }">
                  <div class="tree-node-content">
                    <!-- 根据类型显示不同图标 -->
                    <el-icon v-if="data.type === 2" class="device-icon">
                      <Monitor /> <!-- 使用设备图标 -->
                    </el-icon>
                    <el-icon v-else-if="data.type === 1" class="company-icon">
                      <OfficeBuilding /> <!-- 使用公司/组织图标 -->
                    </el-icon>
                    <el-icon v-else class="default-icon">
                      <Folder /> <!-- 默认文件夹图标 -->
                    </el-icon>
                    <span class="node-label">{{ data.name }}</span>
                  </div>
                </template>
          </el-tree-select>   
        </el-form-item>

        <el-form-item :label="$t('message.reaTimePlot.monitorVariable')">
          <VariableSelector
            v-model="selectedVariables"
            :device-id="currentDeviceId"
            @change="handleVariableChange"
            class="filter-control variable-selector"
            style="width: 300px"
          />
        </el-form-item>

        <el-form-item :label="$t('message.reaTimePlot.MonitoringDuration')">
          <el-input-number v-model="monitoringDuration" :min="1" />
        </el-form-item>

        <el-form-item>
          <el-button type="primary" @click="rollCallClick">
            {{ $t("message.reaTimePlot.rollCall") }}
          </el-button>
        </el-form-item>

        <el-form-item>
          <el-button type="primary" @click="handleTrack">
            {{ $t("message.reaTimePlot.track") }}
          </el-button>
        </el-form-item>
      </el-form>

      <!-- 数据列表区域 -->
      <div class="data-table">
        <el-table :data="tableData" border style="width: 100%" height="280">
          <el-table-column
            prop="timestamp"
            :label="$t('message.reaTimePlot.time')"
            width="180"
          ></el-table-column>
          <el-table-column
            v-for="variable in selectedVariables"
            :key="variable.name"
            :prop="variable.name"
            :label="variable.display_name"
          ></el-table-column>
        </el-table>

        <el-pagination
          v-model:current-page="pagination.currentPage"
          v-model:page-size="pagination.pageSize"
          :total="pagination.total"
          :page-sizes="[5, 10, 15, 20, 30, 50]"
          layout="total, sizes, prev, pager, next, jumper"
          @current-change="handlePageChange"
          @size-change="handleSizeChange"
          :background="true"
          class="pagination"
        />

        <!-- 指定间隔和指定时间追踪 -->
        <el-dialog
          v-model="TrackDialogVisible"
          :title="$t('message.reaTimePlot.track')"
          width="400px"
        >
          <div class="edit-dialog-content">
            <el-form :model="trackForm" label-width="120px">
              <el-form-item :label="$t('message.reaTimePlot.interval')">
                <el-input-number v-model="trackForm.intervalValue" :min="1" />
              </el-form-item>

              <el-form-item :label="$t('message.reaTimePlot.duration')">
                <el-input-number v-model="trackForm.durationValue" :min="1" />
              </el-form-item>
            </el-form>
            <div class="dialog-footer">
              <el-button @click="TrackDialogVisible = false">
                {{ $t("message.common.cancel") }}
              </el-button>
              <el-button type="primary" @click="confirmTrack">
                {{ $t("message.common.confirm") }}
              </el-button>
            </div>
          </div>
        </el-dialog>
      </div>
    </div>
  </div>
</template>
<script setup>
import {ref,reactive,onMounted,onBeforeUnmount,onUnmounted,computed} from "vue";
import { onBeforeRouteLeave } from "vue-router"; // 从 vue-router 导入
import * as echarts from "echarts";
import { ElMessage, ElMessageBox } from "element-plus";
import { getCompanyDeviceTree } from "@/api/CompanyManager";
import { heart } from "@/api/UserManager";
import { useI18n } from "vue-i18n";
//import baseConfig from "@/public/config.json";
import { useSignalR } from "@/utils/signalR";
import { getToken } from "@/utils/auth"; // 如果有认证系统
import VariableSelector from "@/components/views/VariableSelector.vue";
import { rollCall } from "@/api/DeviceManagement";
import { Monitor, OfficeBuilding, Folder} from '@element-plus/icons-vue'
import { formatValue } from "@/api/DeviceManagement";
// 正确写法（使用反引号 ` 和 ${}`）
//const url = `${baseConfig.apiBaseUrl}/subscriptionHub`;
const url = ref('');
// 初始化 SignalR
const {
  isConnected,
  error,
  isReconnecting, // 暴露重连状态
  reconnectAttempts, // 暴露重连尝试次数
  reconnectDelay, // 暴露当前重连延迟时间
  start,
  stop,
  invoke,
  on,
  off,
  onConnected,
} = useSignalR(url, {
  accessTokenFactory: () => getToken() || "",
  automaticReconnect: true, // 如果底层库支持
});

const monitoringDurationOptions = ref([]);

// 分页
const pagination = reactive({
  currentPage: 1,
  pageSize: 5,
  total: 0,
});

const loadOptions = () => {
  monitoringDurationOptions.value = [
    { value: 30, label: "30 " + t("message.reaTimePlot.minute") },
    { value: 60, label: "1 " + t("message.reaTimePlot.hour") },
    { value: 120, label: "2 " + t("message.reaTimePlot.hour") },
    { value: 180, label: "3 " + t("message.reaTimePlot.hour") },
    { value: 240, label: "4 " + t("message.reaTimePlot.hour") },
    { value: 300, label: "5 " + t("message.reaTimePlot.hour") },
  ];
};

const { t } = useI18n();
// 设备列表
const deviceList = ref([]);
// 设备树相关
const selectedDevice = ref([]);
const deviceTreeOptions = ref([]);
/* const deviceTreeProps = {
  value: "id",
  label: "name",
  children: "children",
  checkStrictly: true, // 关键修改：允许单击选择任意节点
  emitPath: false, // 关键修改：只返回选中的节点ID
}; */
// 树选择器配置
const treeSelectProps = {
  value: 'id',    
  label: 'name',
  children: 'children',
  parentId:'parentId',
};



// 修改：selectedVariables 现在存储对象数组
const selectedVariables = ref([]);
// 图表相关
const chartRef = ref(null);
let chartInstance = null;
// 表格数据
const tableData = ref([]);
let timer = null;
const refreshInterval = 30 * 1000; // 30秒刷新一次数据
let heartTimer = null;
let trackTimer = null;
const refreshHeartInterval = 10 * 60 * 1000; // 十分钟刷新一次数据
const currentDeviceId = ref();

// 在script setup部分添加以下变量
const monitoringDuration = ref(1); // 最大缓存小时数
const MAX_CACHE_HOURS = ref(0); // 最大缓存小时数
const cachedData = ref([]); // 缓存所有接收到的数据
const isSubmitting = ref(false); // 用于控制提交状态
const treeSelectRef = ref(null);
// Add these to your existing script
const TrackDialogVisible = ref(false);
const trackForm = ref({
  intervalValue: 1,
  durationValue: 1,
});



import { getConfig } from '@/utils/configTool';
// 3. 异步加载配置函数
async function loadConfig() {
  try {
      // 获取完整配置对象
    const config = await getConfig();
    // 解构使用
    // 4. 赋值 SignalR url
    url.value = config.signalRUrl;
    //console.log('SignalR 地址配置成功：', signalRUrl.value);
  } catch (error) {
    console.error('加载配置文件失败：', error); 
  }
}


// 获取变量名数组（用于内部处理）
const getVariableNames = () => {
  return selectedVariables.value.map(v => v.name);
};

// 获取显示名数组（用于显示）
const getDisplayNames = () => {
  return selectedVariables.value.map(v => v.display_name);
};
//得到mult
const getMultAndDecimal = (varName) => {
  // 查找特定变量名的对象
  const variable = selectedVariables.value.find(v => v.name === varName);
  // 同时返回 mult 和 var_decimal，各自设置默认值
  return {
    mult: variable?.mult ?? 1,       // mult 默认返回 1
    var_decimal: variable?.var_decimal ?? 0 // var_decimal 默认返回空字符串（可改为 0/NaN 等）
  };
};

//打开追踪窗体，设置定时，定长追踪变量
const handleTrack = () => {
  // 验证变量选择
  if (selectedVariables.value.length <= 0) {
    ElMessage.error(t("message.reaTimePlot.selectVariable"));
    return;
  }
  TrackDialogVisible.value = true;
};

//执行追踪
const confirmTrack = async () => {
  // 先停止可能存在的旧定时器
  if (trackTimer) {
    clearInterval(trackTimer);
    trackTimer = null;
  }

  // 验证连接状态
  if (!isConnected) {
    ElMessage.error(t("message.reaTimePlot.connectError"));
    return;
  }

  // 验证变量选择
  if (selectedVariables.value.length <= 0) {
    ElMessage.error(t("message.reaTimePlot.selectVariable"));
    return;
  }

  try {
    // 立即执行一次数据获取
    await trackCmdMsg();

    // 启动心跳定时器
    trackTimer = setInterval(async () => {
      try {
        await trackCmdMsg();
      } catch (error) {
        console.error("定时数据获取失败:", error);
        // 出错时自动停止定时器
        if (trackTimer) {
          clearInterval(trackTimer);
          trackTimer = null;
        }
        ElMessage.error(t("message.reaTimePlot.dataProcessError"));
      }
    }, trackForm.value.intervalValue * 1000);

    // 设置自动停止时间（分钟转毫秒）
    const durationMs = trackForm.value.durationValue * 60 * 1000;
    setTimeout(() => {
      if (trackTimer) {
        clearInterval(trackTimer);
        trackTimer = null;
        console.log("定时器已自动停止");
        ElMessage.success(t("message.reaTimePlot.trackingCompleted"));
      }
    }, durationMs);
    TrackDialogVisible.value = false;
    ElMessage.success(t("message.reaTimePlot.trackingStarted"));
  } catch (error) {
    console.error("启动实时跟踪失败:", error);
    // 确保出错时定时器被清理
    if (trackTimer) {
      clearInterval(trackTimer);
      trackTimer = null;
    }
    ElMessage.error(t("message.reaTimePlot.dataProcessError"));
  }
};

const reconnectMessage = computed(() => {
  return t("message.reaTimePlot.reconnecting", {
    attempts: reconnectAttempts.value,
    seconds: Math.round(reconnectDelay.value / 1000),
  });
});

const handlePageChange = (val) => {
  pagination.currentPage = val;
  updateTable(cachedData.value); // 重新加载当前页数据
};

const handleSizeChange = (val) => {
  pagination.pageSize = val;
  pagination.currentPage = 1; // 重置到第一页
  updateTable(cachedData.value); // 重新加载数据
};

const handleVariableChange = (newSelection) => {
  try {
    // 1. 验证选择数量
    if (newSelection.length > 30) {
      ElMessage.error(t("message.reaTimePlot.SelectVarMax"));
      return;
    }

    // 2. 处理变量变化
    if (newSelection && newSelection.length > 0) {
      // 获取当前已选择的变量（变化前）
      const currentVars = selectedVariables.value;

      // 找出被删除的变量
      const removedVars = currentVars.filter(v => 
        !newSelection.some(nv => nv.name === v.name)
      );
      // 找出新增的变量
      const addedVars = newSelection.filter(nv => 
        !currentVars.some(v => v.name === nv.name)
      );

      // 3. 更新缓存数据
      if (cachedData.value && cachedData.value.length > 0) {
        // 从缓存中移除被删除变量的数据
        if (removedVars.length > 0) {
          cachedData.value = cachedData.value.map((item) => {
            removedVars.forEach((variable) => {
              delete item[variable.name];
              delete item[`${variable.name}_Error`];
            });
            return item;
          });
        }

        // 为新变量初始化数据（如果需要）
        if (addedVars.length > 0) {
          cachedData.value = cachedData.value.map((item) => {
            addedVars.forEach((variable) => {
              item[variable.name] = item[variable.name] ?? "--";
              item[`${variable.name}_Error`] = item[`${variable.name}_Error`] ?? 0;
            });
            return item;
          });
        }
      }

      // 4. 更新图表和表格
      updateChart(cachedData.value);
      updateTable(cachedData.value);

      // 5. 更新当前选中的变量
      selectedVariables.value = newSelection;
      //订阅
      sendMessage();
    } else {
      clearAllData();
      // 清空选择的情况
      //clearChart();
      //tableData.value = [];
      //selectedVariables.value = [];
    }
  } catch (error) {
    ElMessage.error(`${error}`);
  }
};


// 新增：清空所有数据并停止订阅
const clearAllData = async () => {
  try {
    // 1. 停止当前的订阅
    await stopSubscription();
    
    // 2. 清空图表
    clearChart();
    
    // 3. 清空表格数据
    tableData.value = [];
    
    // 4. 清空缓存数据
    cachedData.value = [];
    pagination.total = 0;
    
    // 5. 清空选中的变量
    selectedVariables.value = [];
    
    // 6. 重置分页
    pagination.currentPage = 1;
    
    console.log("已清空所有数据并停止订阅");
  } catch (error) {
    console.error("清空数据时出错:", error);
    ElMessage.error(t("message.reaTimePlot.dataClearError"));
  }
};

// 新增：停止订阅函数
const stopSubscription = async () => {
  if (!isConnected.value) return;
  
  try {
    const token = getToken();
    if (!token || !selectedDevice.value.deviceCode) {
      return;
    }

    const params = {
      token: token,
      deviceCode: selectedDevice.value.deviceCode,
      imei: null,
      Dir: "d",
      type: 1, // 实时数据订阅
      priority: 1,
      timer: new Date().toISOString(),
      r_data: [], // 空数组表示取消所有订阅
    };

    const jsonString = JSON.stringify(params);
    await invoke("ReleaseData", jsonString);
    console.log("订阅已停止");
  } catch (error) {
    console.error("停止订阅失败:", error);
    // 这里不显示错误消息，因为清空变量时停止订阅失败不影响主要功能
  }
};

// 初始化图表
const initChart = () => {
  if (chartRef.value) {
    chartInstance = echarts.init(chartRef.value);
    const option = {
      title: {
        text: t("message.reaTimePlot.deviceVariableMonitor"),
      },
      tooltip: {
        trigger: "axis",
      },
      legend: {
        data: [],
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "3%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: [],
      },
      yAxis: {
        type: "value",
      },
      series: [],
    };
    chartInstance.setOption(option);
  }
};

// 格式化设备树数据
const formatDeviceTree = (treeData) => {
  return treeData.map((item) => {
    const node = {
      id: item.id,
      name: item.name,
      type: item.type,
      key: item.key,
      deviceCode: item.data?.device_code || "",
      parentId: item.parentId,
      parentKey: item.parentKey,
    };

    if (item.children && item.children.length > 0) {
      node.children = formatDeviceTree(item.children);
    }

    return node;
  });
};

// 加载设备树
const loadDeviceTree = async () => {
  try {
    const params = { companyId: null };
    const res = await getCompanyDeviceTree(params);

    if (res.success) {
      deviceList.value = res.data.list;

      deviceTreeOptions.value = res.data.list?.length
        ? formatDeviceTree(res.data.list)
        : [];

      if (deviceTreeOptions.value.length === 0) {
        //ElMessage.warning('未找到设备树数据');
      }
    } else {
      ElMessage.error(res.msg || t("message.reaTimePlot.loadDeviceTreeFailed"));
    }
  } catch (error) {
    ElMessage.error(t("message.reaTimePlot.loadDeviceTreeFailed"));
  }
};


// 存储当前选中的节点数据
const currentSelectedNode = ref(null);

// 节点点击事件 - 获取完整节点数据
const handleNodeClick = (nodeData) => {
  
  console.log('点击节点:', nodeData);
  currentSelectedNode.value = nodeData;
  
  // 类型2：设备节点 - 直接选中
  if (nodeData.type === 2) {
    currentDeviceId.value = nodeData.id;
    handleDeviceIdChange(nodeData.id);
    
    return;
  }
  
  // 非设备节点且没有子节点：提示
  if (!nodeData.children || nodeData.children.length === 0) {
    ElMessage.warning(t('message.realTimeData.selectDeviceOnly'));
  }  
  //ElMessage.error(`${nodeData} --${nodeData.id}--${nodeData.type}-1- handleNodeClick`);
 // currentSelectedNode.value = nodeData;
  
  // 这里可以立即处理节点数据，或者等待 @change 事件
};

// ID变化事件
const handleDeviceIdChange = async (selectedId) => {    
    if (!selectedId) {
      currentDeviceId.value = null;
      currentSelectedNode.value = null;
      selectedDevice.value = null
      tableData.value = [];
      cachedData.value = [];
      return;
    }  
    
    // 使用存储的节点数据
    if (currentSelectedNode.value && currentSelectedNode.value.type === 2) {
      const nodeData = currentSelectedNode.value;
      currentDeviceId.value = nodeData.id;         
      selectedDevice.value = currentSelectedNode.value;          
      //ElMessage.error(`${selectedDevice.value.deviceCode} handleDeviceIdChange`);
    } else {
      currentDeviceId.value = null;
      selectedDevice.value = null
      tableData.value = [];
      cachedData.value = [];
      //ElMessage.warning(t('message.realTimeData.selectDeviceOnly'));
    }
};

/* 
const handleDeviceChange = (selectedId) => {
  if (!selectedId) {
    return;
  }
  selectedVariables.value = [];
  currentDeviceId.value = 0;
  // 查找选中的节点
  const findNode = (nodes, id) => {
    for (const node of nodes) {
      if (node.id === id && node.type === 2) {
        return node;
      }
      if (node.children) {
        const found = findNode(node.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  const selectedNode = findNode(deviceTreeOptions.value, selectedId);

  if (!selectedNode) {
    return;
  }
  // 检查设备类型（type=2表示设备）
  if (selectedNode.type === 2) {
    selectedDevice.value = selectedNode;
    // ElMessage.error(`${selectedDevice.value.deviceCode}-handleDeviceChange`);
    currentDeviceId.value = selectedNode.id;
    // ElMessage.error(`${currentDeviceId.value} 不是设备类型`);
    //  loadSlave();
  } else {
    //ElMessage.error(`${selectedNode.name} 不是设备类型`);
  }
};
 */
const formatDate = (dateString) => {
  if (!dateString) return "";

  const date = new Date(dateString);

  return new Intl.DateTimeFormat("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  })
    .format(date)
    .replace(/\//g, "-") // 将斜杠替换为横杠
    .replace(/(\d+):(\d+):(\d+)/, "$1:$2:$3"); // 保持时间格式
};


// 解析数据，并将解析后的数据整理到缓存数组中
// 处理接收到的设备数据
const processDeviceData = (jsonStr) => {
  try {
    const receivedData = JSON.parse(jsonStr);
    const receivedTime = new Date(receivedData.time);
    const formattedTime = formatDate(receivedTime);

    // 查找是否已存在相同时间戳的数据行
    const existingDataIndex = cachedData.value.findIndex(
      item => item.timestamp === formattedTime && item.DeviceCode === receivedData.DeviceCode
    );

    let newDataItem;

    if (existingDataIndex !== -1) {
      // 如果已存在相同时间戳的数据，则更新该行数据
      newDataItem = cachedData.value[existingDataIndex];
    } else {
      // 否则创建新的数据行
      newDataItem = {
        timestamp: formattedTime,
        rawTime: receivedTime,
        DeviceCode: receivedData.DeviceCode,
        imei: receivedData.imei,
        Type: receivedData.Type,
        Priority: receivedData.Priority,
      };
    }

    // 处理变量数据
    if (receivedData.Vars && Array.isArray(receivedData.Vars)) {
      receivedData.Vars.forEach((variable) => {
        if (variable && variable.Name && variable.Error == 0) {
          //newDataItem[variable.Name] = (variable.Value ?? 0) * getMult(variable.Name);
          const { mult, var_decimal } = getMultAndDecimal(variable.Name);
        //  console.error(`processDeviceData --variable.Value:${variable.Value};mult:${mult}--var_decimal: ${var_decimal}`);
          newDataItem[variable.Name] = formatValue(variable.Value,mult, var_decimal);
          newDataItem[`${variable.Name}_Error`] = variable.Error ?? 0;
        }
      });
    }

    if (existingDataIndex === -1) {
      // 如果是新数据行，添加到缓存
      cachedData.value.push(newDataItem);
    } else {
      // 如果是更新现有数据行，替换原数据
      cachedData.value[existingDataIndex] = newDataItem;
    }

    pagination.total = cachedData.value.length;

    // 清理过期数据
    const now = new Date();
    MAX_CACHE_HOURS.value = monitoringDuration.value * 60 * 60 * 1000;
    const cacheTimeAgo = new Date(now.getTime() - MAX_CACHE_HOURS.value);
    cachedData.value = cachedData.value.filter(
      (item) => item.rawTime > cacheTimeAgo
    );

    // 按时间降序排序
    cachedData.value.sort((a, b) => b.rawTime - a.rawTime);

    return cachedData.value;
  } catch (error) {
    console.error("数据处理错误:", error);
    ElMessage.error(t("message.reaTimePlot.dataProcessError"));
    return [];
  }
};

// const processDeviceData = (jsonStr) => {
//   try {
//     if (navigator.deviceMemory) {
//       const availableMemory = navigator.deviceMemory;
//       // 当内存小于2GB时警告（可根据需要调整阈值）
//       if (availableMemory < 2) {
//         ElMessage.warning(
//           t("message.reaTimePlot.notEnoughMemory", {
//             memorys: availableMemory.toFixed(2), // 显示两位小数
//           })
//         );
//       }
//     }

//     const receivedData = JSON.parse(jsonStr);
//     // 1. 解析并格式化新数据
//     const receivedTime = new Date(receivedData.time);
//     const formattedTime = formatDate(receivedTime); // receivedTime.toLocaleString(); //.toLocaleTimeString();

//     const newDataItem = {
//       timestamp: formattedTime,
//       rawTime: receivedTime, // 保留原始时间对象用于排序
//       DeviceCode: receivedData.DeviceCode,
//       imei: receivedData.imei,
//       Type: receivedData.Type,
//       Priority: receivedData.Priority,
//     };

//     // 3. 处理变量数据（添加保护性判断）
//     if (receivedData.Vars && Array.isArray(receivedData.Vars)) {
//       receivedData.Vars.forEach((variable) => {
//         if (variable && variable.Name && variable.Error == 0) {
//           newDataItem[variable.Name] = variable.Value ?? 0;
//           newDataItem[`${variable.Name}_Error`] = variable.Error ?? 0;
//         }
//       });
//     }
//     // 2. 添加到缓存
//     cachedData.value.push(newDataItem);
//     pagination.total = cachedData.value.length;
//     // 3. 清理过期数据（超过6小时）
//     const now = new Date();
//     MAX_CACHE_HOURS.value = monitoringDuration.value * 60 * 60 * 1000;
//     const sixHoursAgo = new Date(now.getTime() - MAX_CACHE_HOURS.value);
//     cachedData.value = cachedData.value.filter(
//       (item) => item.rawTime > sixHoursAgo
//     );

//     // 4. 按时间降序排序
//     cachedData.value.sort((a, b) => b.rawTime - a.rawTime);

//     // 5. 返回最新50条数据（可根据需要调整）
//     return cachedData.value; //cachedData.value.slice(0, 50);
//   } catch (error) {
//     ElMessage.info(`${error}`);
//   }
// };


const aggregateData = (data, maxPoints = 3000) => {
      if (data.length <= maxPoints) {
        return data;
      }

      console.log(`数据量 ${data.length} 超过 ${maxPoints}，进行聚合处理`);
      
      const aggregatedData = [];
      const interval = Math.ceil(data.length / maxPoints);
      
      for (let i = 0; i < data.length; i += interval) {
        const chunk = data.slice(i, i + interval);
        if (chunk.length === 0) continue;
        
        // 取中间点的时间戳
        const middleIndex = Math.floor(chunk.length / 2);
        const aggregatedItem = {
          ...chunk[middleIndex], // 使用中间点的数据作为基础
          timestamp: chunk[middleIndex].timestamp
        };
        
        // 对每个变量进行聚合计算
        selectedVariables.value.forEach(variable => {
          const values = chunk
            .map(item => parseFloat(item[variable.name]))
            .filter(value => !isNaN(value));
          
          if (values.length > 0) {
            // 计算平均值
            const avg = values.reduce((sum, val) => sum + val, 0) / values.length;
            aggregatedItem[variable.name] = Number(avg.toFixed(4));
          } else {
            aggregatedItem[variable.name] = null;
          }
        });
        
        aggregatedData.push(aggregatedItem);
      }
      
      console.log(`聚合后数据量: ${aggregatedData.length}`);
      return aggregatedData;
    };

const updateChart = (srcData) => {
  if (!chartInstance || !selectedVariables.value.length) return;

  const data = aggregateData(srcData, 3000);
  // 1. 按时间戳升序排序（确保小时间在左，大时间在右）
  const sortedData = [...data].sort(
    (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
  );

  // 2. 准备图表数据
  const timeData = sortedData.map((item) => item.timestamp);
  const seriesData = selectedVariables.value.map((variable) => ({
    name: variable.display_name,  //使用显示名称
    type: "line",
    symbol: "circle", // 修改为圆圈
    symbolSize: 6, // 圆圈大小
    smooth: true,
    lineStyle: {
      width: 2, // 线条粗细
    },
    itemStyle: {
      borderWidth: 1, // 圆圈边框宽度
      borderColor: "#fff", // 圆圈边框颜色
    },
    data: sortedData.map((item) => {
      const value = parseFloat(item[variable.name]);
      return isNaN(value) ? null : value; // 处理无效数据
    }),
  }));

  // 3. 图表配置
  const option = {
    legend: {
      data: getDisplayNames(),
      selected: Object.fromEntries(
        selectedVariables.value.map((variable) => [variable.display_name, true])
      ),
    },
    xAxis: {
      type: "category",
      boundaryGap: false,
      data: timeData,
      axisLabel: {
        rotate: 30,
        formatter: (value, index) =>
          index % Math.ceil(timeData.length / 10) === 0 ? value : "", // 动态间隔显示
      },
    },
    yAxis: {
      type: "value",
      scale: true,
      axisLine: {
        show: true,
      },
      splitLine: {
        show: true,
        lineStyle: {
          type: "dashed",
        },
      },
    },
    series: seriesData,
    dataZoom: [
      { type: "inside", start: 0, end: 100 },
      { start: 0, end: 100 },
    ],
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "cross",
        label: {
          backgroundColor: "#6a7985",
        },
      },
    },
    grid: {
      left: "3%",
      right: "4%",
      bottom: "3%",
      containLabel: true,
    },
  };

  // 4. 应用配置
  chartInstance.setOption(option, { notMerge: true });

  // 5. 自动调整大小
  setTimeout(() => chartInstance.resize(), 100);
};

// 优化后的updateTable方法
// 更新表格数据方法，加入分页逻辑
const updateTable = (data) => {
  // 计算分页起始和结束位置
  const startIndex = (pagination.currentPage - 1) * pagination.pageSize;
  const endIndex = startIndex + pagination.pageSize;

  // 获取当前页的数据
  const pagedData = data.slice(startIndex, endIndex);

  // 确保表格列与当前选择的变量一致
  tableData.value = pagedData.map((item) => {
    const row = { timestamp: item.timestamp };
    selectedVariables.value.forEach((variable) => {
      row[variable.name] = item[variable.name] ?? "--";
    });
    return row;
  });

  // 更新总条数
  pagination.total = data.length;
};

// 清空图表
const clearChart = () => {
  if (chartInstance) {
    chartInstance.clear();
  }
};

// 开始自动刷新
const startAutoRefresh = () => {
  stopAutoRefresh();
  //首次订阅
  sendMessage();

  timer = setInterval(() => {
    if (selectedVariables.value.length > 0) {
      sendMessage();
    }
  }, refreshInterval);
  //启动心跳
  heartTimer = setInterval(() => {
    heart();
  }, refreshHeartInterval);
};

// 停止自动刷新
const stopAutoRefresh = () => {
  if (timer) {
    clearInterval(timer);
    timer = null;
  }

  if (heartTimer) {
    clearInterval(heartTimer);
    heartTimer = null;
  }
};


// 生命周期钩子
onMounted(async () => {
  await loadConfig();
  loadOptions();
  initChart();
  await loadDeviceTree();
  startAutoRefresh();
  await start();
  // 注册消息处理器
  on("ReceiveMessage", handleNotification);
  /*   on('Init', (msg) => {
      ElMessage.info(`${msg} Init onMounted`);      
  }); */
});

onBeforeUnmount(() => {
  if (chartInstance) {
    chartInstance.dispose();
  }
  stopAutoRefresh();
});

// //追踪
// const trackCmdMsg = async () => {
//   if (!isConnected) {
//     ElMessage.error(t("message.reaTimePlot.connectError"));
//     return;
//   }
//   try {
//     const token = getToken();
//     // 1. 构造请求数据
//     const requestData = [
//       {
//         lang :  localStorage.getItem('lang') !=null ? localStorage.getItem('lang') : 'zh-CN' ,
//         token: token,
//         deviceCode: selectedDevice.value.deviceCode,
//         imei: selectedDevice.value.imei,
//         type: 4, // 1=定时数据
//         dir: "Down",
//         timer: new Date().toISOString(),
//         priority: 1,
//         r_data: [],
//         w_data: [],
//       },
//     ];

//     //2.追加点名数据
//     getVariableNames().forEach((varName) => {
//       if (
//         !requestData[0].r_data.some((existing) => existing.name === varName)
//       ) {
//         requestData[0].r_data.push({ name: varName });
//       }
//     });

//     await rollCall(requestData);
//   } catch (error) {
//     console.log(t("message.reaTimePlot.dataProcessError"));
//   }
// };

const trackCmdMsg = async () => {
  if (!isConnected) {
    ElMessage.error(t("message.reaTimePlot.connectError"));
    return;
  }
  try {
    const token = getToken();
    
    // 获取所有变量名
    const variableNames = getVariableNames();
    if (variableNames.length === 0) {
      ElMessage.warning(t("message.reaTimePlot.noVariables"));
      return;
    }

    // 将变量名按每5个分组
    const variableChunks = chunkArray(variableNames, 5);
    
    // 使用相同的时间戳，确保同一批次的数据可以合并
    const batchTimestamp = new Date().toISOString();
    
    // 为每个变量包创建请求
    const rollCallPromises = [];
    
    for (let i = 0; i < variableChunks.length; i++) {
      const chunk = variableChunks[i];
      
      const requestData = [
        {
          lang: localStorage.getItem('lang') != null ? localStorage.getItem('lang') : 'zh-CN',
          token: token,
          deviceCode: selectedDevice.value.deviceCode,
          imei: selectedDevice.value.imei,
          type: 4, // 点名类型
          dir: "Down",
          timer: batchTimestamp, // 使用相同的时间戳
          priority: 1,
          r_data: chunk.map(name => ({ name })),
          w_data: [],
        },
      ];

      rollCallPromises.push(
        rollCall(requestData)
          .then(() => {
            console.log(`第${i + 1}/${variableChunks.length}包点名成功，变量数量: ${chunk.length}`);
          })
          .catch(error => {
            console.error(`第${i + 1}包点名失败:`, error);
            throw error;
          })
      );
      
      // 可选：添加延迟避免请求过于密集
      if (i < variableChunks.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    // 并行发送所有点名请求
    await Promise.all(rollCallPromises);
    
    console.log(`点名请求完成，共 ${variableChunks.length} 个包，${variableNames.length} 个变量`);
    
  } catch (error) {
    console.error('点名操作失败:', error);
    // ElMessage.error(
    //   error.response?.data?.message || 
    //   error.message || 
    //   t("message.reaTimePlot.dataProcessError")
    // );
  }
};

// 辅助函数：将数组按指定大小分块
const chunkArray = (array, chunkSize) => {
  const chunks = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
};

// //点名
//点名
const rollCallClick = async () => {
  // 验证变量选择
  if (selectedVariables.value.length <= 0) {
    ElMessage.error(t("message.reaTimePlot.selectVariable"));
    return;
  }

  if (!isConnected) {
    ElMessage.error(t("message.reaTimePlot.connectError"));
    return;
  }

  try {
    const token = getToken();
    
    // 获取所有变量名
    const variableNames = getVariableNames();
    if (variableNames.length === 0) {
      ElMessage.warning(t("message.reaTimePlot.noVariables"));
      return;
    }

    // 将变量名按每5个分组
    const variableChunks = chunkArray(variableNames, 5);
    
    // 使用相同的时间戳，确保同一批次的数据可以合并
    const batchTimestamp = new Date().toISOString();
    
    // 为每个变量包创建请求
    const rollCallPromises = [];
    
    for (let i = 0; i < variableChunks.length; i++) {
      const chunk = variableChunks[i];
      
      const requestData = [
        {
          token: token,
          deviceCode: selectedDevice.value.deviceCode,
          imei: selectedDevice.value.imei,
          type: 4, // 点名类型
          dir: "Down",
          timer: batchTimestamp, // 使用相同的时间戳
          priority: 1,
          r_data: chunk.map(name => ({ name })),
          w_data: [],
        },
      ];

      rollCallPromises.push(
        rollCall(requestData)
          .then(() => {
            console.log(`第${i + 1}/${variableChunks.length}包点名成功，变量数量: ${chunk.length}`);
          })
          .catch(error => {
            console.error(`第${i + 1}包点名失败:`, error);
            throw error;
          })
      );
      
      // 可选：添加延迟避免请求过于密集
      if (i < variableChunks.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    // 并行发送所有点名请求
    await Promise.all(rollCallPromises);
    
    
  } catch (error) {
    console.error('点名操作失败:', error);
    // ElMessage.error(
    //   error.response?.data?.message || 
    //   error.message || 
    //   t("message.reaTimePlot.dataProcessError")
    // );
  }
};
 
// const rollCallClick = async () => {
//   // 验证变量选择
//   if (selectedVariables.value.length <= 0) {
//     ElMessage.error(t("message.reaTimePlot.selectVariable"));
//     return;
//   }

//   if (!isConnected) {
//     ElMessage.error(t("message.reaTimePlot.connectError"));
//     return;
//   }

//   //ElMessage.error(`${selectedDevice.value.deviceCode} rollCallClick`);

//   try {
//     const token = getToken();
//     // 1. 构造请求数据
//     const requestData = [
//       {
//         token: token,
//         deviceCode: selectedDevice.value.deviceCode,
//         imei: selectedDevice.value.imei,
//         type: 4, // 1=定时数据
//         dir: "Down",
//         timer: new Date().toISOString(),
//         priority: 1,
//         r_data: [],
//         w_data: [],
//       },
//     ];

//     //2.追加点名数据
//     getVariableNames().forEach((varName) => {
//       if (
//         !requestData[0].r_data.some((existing) => existing.name === varName)
//       ) {
//         requestData[0].r_data.push({ name: varName });
//       }
//     });

//     await rollCall(requestData);
//   } catch (error) {
//     ElMessage.error(t("message.reaTimePlot.dataProcessError"));
//   }
// };

// 消息输入
//const notifications = ref([])

// 自定义消息处理函数
const handleNotification = async (message) => {
  //{ "dir": "u", "time": "2025-06-24T17:46:59", "DeviceCode": "0000000020000001", "imei": "865735071680370", "Type": 1, "Priority": 1, "Vars": [ { "Error": 1, "Name": "ALF1", "Value": 0 }, { "Error": 1, "Name": "ALF2", "Value": 0 }, { "Error": 1, "Name": "ALF3", "Value": 0 }, { "Error": 1, "Name": "ALF4", "Value": 0 } ] }

  try {
    
    // 1. 处理并获取最新数据
    const processedData = processDeviceData(message);

    // 2. 更新图表和表格
    updateChart(processedData);
    updateTable(processedData);
  } catch (error) {
    console.error("处理数据错误:", error);
    ElMessage.error(t("message.reaTimePlot.dataProcessError"));
  }
};

const RealeaseSubscribe = async () => {
  if (!isConnected) return true; // 返回true表示无需处理

  try {

    const token = getToken();
    if (!token || !selectedDevice.value.deviceCode) {
      return true; // 没有需要释放的资源
    }

    const params = {
      token: token,
      deviceCode: selectedDevice.value.deviceCode,
      imei: null,
      Dir: "d",
      type: 1,
      priority: 1,
      timer: new Date().toISOString(),
      r_data: [],
    };

    const jsonString = JSON.stringify(params);    
    await invoke("ReleaseData", jsonString);
    
    return true; // 表示成功
  } catch (err) {
    console.error("释放订阅失败:", err);
    ElMessage.error(t("message.reaTimePlot.releaseFailed"));
    return false; // 表示失败
  }
};

onBeforeRouteLeave(async (to, from, next) => {
  // 如果没有选择变量，直接离开
  if (selectedVariables.value.length === 0) {
    next();
    return;
  }

  try {
    // 显示确认对话框
    await ElMessageBox.confirm(
      t("message.reaTimePlot.leaveConfirm"),
      t("message.common.tip"),
      {
        confirmButtonText: t("message.common.confirm"),
        cancelButtonText: t("message.common.cancel"),
        type: "warning",
      }
    );

    // 用户确认离开，开始释放订阅
    isSubmitting.value = true;
    stopAutoRefresh();
    ElMessage.info(t("message.reaTimePlot.releasingSubscription"));

    // 调用释放订阅
    await RealeaseSubscribe();

    // 完成后允许离开
    next();
  } catch (error) {
    if (error !== "cancel") {
      console.error("离开页面错误:", error);
      ElMessage.error(t("message.reaTimePlot.leaveError"));
    }
    // 取消导航
    next(false);
  } finally {
    isSubmitting.value = false;
  }
});



// 组件卸载时清理
onUnmounted(async () => {
  //退出时调用
  if (selectedVariables.value.length > 0) {
    isSubmitting.value = true;
    try {
      //await RealeaseSubscribe()
      if (trackTimer) {
        clearInterval(trackTimer);
        trackTimer = null;
      }
    } catch (error) {
      console.error("组件卸载时释放订阅失败:", error);
    } finally {
      isSubmitting.value = false;
    }
  }

  try {
    unsubscribe();
    off("ReceiveMessage", handleNotification);
    stop();
  } catch (error) {
    console.error("fail:", error);
  }
});

// 发送消息
const sendMessage = async () => {
  if (!isConnected) return;

  if (selectedVariables.value.length <= 0) return;

  if (!error) {
    ElMessage.error(`${error}`);
  }
  try {
    const token = getToken();
    const params = {
      token: token, // 从本地存储获取token
      deviceCode: selectedDevice.value.deviceCode,
      imei: null,
      Dir: "d", //下行数据
      type: 1, // 实时数据订阅
      timer: new Date().toISOString(),
      priority: 1,
      r_data: [],
    };

    getVariableNames().forEach((varName) => {
      if (!params.r_data.some((existing) => existing.name === varName)) {
        params.r_data.push({ name: varName });
      }
    });
    const jsonString = JSON.stringify(params);

    await invoke("RequestData", jsonString);
  } catch (err) {
    console.error("Failed to send message:", err);
  }
};

// 注册连接成功回调
const unsubscribe = onConnected(() => {
  sendMessage();
  console.log("连接成功回调执行");
  // 在这里执行连接成功后需要做的操作
  // 比如初始化数据、订阅消息等
});
</script>
<style scoped>
/* 基础容器样式 */
.realtimeplot-container {
  padding: 20px;
  background-color: #f5f7fa;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.chart-container {
  background: #fff;
  padding: 20px;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.control-panel {
  background: #fff;
  padding: 20px;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* 筛选行 - 主要修改部分 */
.filter-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-item {
  display: flex;
  align-items: center;
  flex: 1;
  margin-right: 8px;
}

.filter-item:last-child {
  margin-right: 0;
}

.filter-label {
  width: 100px;
  text-align: right;
  margin-right: 12px;
  font-size: 14px;
  color: #606266;
  flex-shrink: 0;
}

/* 统一控制元素样式 */
.filter-control {
  flex: 1;
  min-width: 180px;
}

/* 级联选择器高度调整 */
.filter-control:deep(.el-cascader .el-input__inner) {
  height: 32px;
  line-height: 32px;
}

/* 变量选择器高度调整 */
.variable-selector:deep(.selector-header) {
  height: 32px;
  min-height: 32px;
  line-height: 32px;
  padding: 0 12px;
  display: flex;
  align-items: center;
}

/* 数据表格区域 */
.data-table {
  width: 100%;
}

/* 变量选择器下拉面板 */
.variable-selector:deep(.selector-dropdown) {
  top: 100% !important;
}

/* 其他样式保持原样 */
.slave-items,
.variable-list,
.variable-items {
  flex: 1;
  overflow-y: auto;
}

.slave-items {
  margin: 0;
  padding: 0;
  list-style: none;
}

.slave-items li {
  padding: 10px 15px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.slave-items li:hover {
  background-color: #f5f7fa;
}

.slave-items li.active {
  background-color: #ecf5ff;
  color: #409eff;
  font-weight: bold;
}

.variable-item {
  display: block;
  margin: 5px 0;
}

.variable-content {
  display: flex;
  flex-direction: column;
}

.variable-name {
  font-weight: 500;
}

.variable-desc {
  font-size: 12px;
  color: #909399;
  margin-top: 2px;
}

.el-pagination {
  padding: 10px 0;
  border-top: 1px solid #e6e6e6;
}

/* 变量选择器容器些处影响高度，宽度等 */
/* .variable-selector-container {
  display: flex;
  height: 32px;
  width: 100%;
  background: white;
} */

.slave-list {
  width: 200px;
  border-right: 1px solid #e6e6e6;
  display: flex;
  flex-direction: column;
}

.pagination {
  margin-top: 15px;
  display: flex;
  justify-content: flex-end;
  background: #fff;
  padding: 10px 16px;
  border-radius: 0 0 4px 4px;
  border-top: 1px solid #ebeef5;
}

/* 在 style 部分添加 */
.connection-status {
  margin-bottom: 16px;
  animation: fadeIn 0.5s;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.dialog-footer {
  margin-top: 20px;
  text-align: right;
}

.el-button--text {
  margin-right: 8px;
}

/* 企业树*/
.tree-node-content {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.device-icon {
  color: #409EFF;
  font-size: 16px;
}

.company-icon {
  color: #67C23A;
  font-size: 16px;
}

.default-icon {
  color: #909399;
  font-size: 16px;
}

.node-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.device-code {
  font-size: 12px;
  color: #909399;
  margin-left: 4px;
}

/* 确保下拉选项中的图标也正确显示 */
:deep(.el-select-dropdown__item) .tree-node-content {
  padding: 0;
}
</style>
